import * as React from 'react';
import { Plus, CircleNotch } from '@phosphor-icons/react';
import { useMutation, useQuery } from 'convex/react';
import { useUser } from '@clerk/tanstack-react-start';
import { api } from '@/convex';
import type { Id } from '@/convex';
import { ContentState, PageHeader } from '@/components/layout';
import {
  deriveCanonicalSource,
  type CanonicalSource,
} from '@/lib/governance/derive-canonical-source';
import { AddCanonicalSourceForm } from './add-canonical-source-form';
import {
  CanonicalSourceRow,
  SourceIdentity,
  sourceLabel,
} from './canonical-source-row';
import settingsStyles from './settings.module.scss';
import s from './canonical-sources.module.scss';

export interface CanonicalSourcesPanelProps {
  projectId: Id<'projects'>;
}

export function CanonicalSourcesPanel({
  projectId,
}: CanonicalSourcesPanelProps) {
  const { user } = useUser();
  const policyData = useQuery(api.projects.getGovernancePolicy, { projectId });
  const updatePolicy = useMutation(api.projects.updateGovernancePolicy);
  const setAdvisorEnabled = useMutation(api.projects.setPrAdvisorEnabled);

  const [adding, setAdding] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [mutationError, setMutationError] = React.useState<string | null>(null);
  const [advisorSaving, setAdvisorSaving] = React.useState(false);
  const advisorEnabled = policyData?.prAdvisorEnabled;

  if (policyData === undefined) {
    return (
      <div className={settingsStyles.detailContent}>
        <ContentState
          className={settingsStyles.loadingState}
          icon={<CircleNotch size={14} className={settingsStyles.spinIcon} />}
          title="Loading governance policy..."
          minHeight={160}
        />
      </div>
    );
  }

  const explicitSources: CanonicalSource[] =
    (policyData.policy?.canonicalSources as CanonicalSource[] | undefined) ??
    [];
  const derived = deriveCanonicalSource(policyData.canonicalPackageName);
  const showDerivedBadge = explicitSources.length === 0 && derived !== null;

  const clerkUserId = user?.id;

  async function callUpdate(next: CanonicalSource[]) {
    if (!clerkUserId) {
      setMutationError('You must be signed in to update sources.');
      return;
    }
    setSubmitting(true);
    setMutationError(null);
    try {
      await updatePolicy({
        projectId,
        clerkUserId,
        canonicalSources: next,
      });
    } catch (err) {
      setMutationError(
        err instanceof Error ? err.message : 'Failed to update sources.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  async function handleAdd(source: CanonicalSource) {
    await callUpdate([...explicitSources, source]);
    setAdding(false);
  }

  async function handleRemove(index: number) {
    await callUpdate(explicitSources.filter((_, i) => i !== index));
  }

  async function handlePromoteDerived() {
    if (!derived) return;
    await callUpdate([derived]);
  }

  async function handleAdvisorToggle() {
    if (!clerkUserId || advisorEnabled === undefined) return;
    setAdvisorSaving(true);
    try {
      await setAdvisorEnabled({
        projectId,
        clerkUserId,
        enabled: !advisorEnabled,
      });
    } finally {
      setAdvisorSaving(false);
    }
  }

  return (
    <div className={settingsStyles.detailContent}>
      <PageHeader
        title="Governance"
        titleAs="h2"
        description="Configure the canonical component libraries Fragments Cloud uses to detect drift and suggest migrations."
        size="md"
      />

      <div className={settingsStyles.section}>
        <p className={settingsStyles.sectionTitle}>
          Canonical component sources
        </p>
        <p className={settingsStyles.sectionDesc}>
          Add the npm packages or directories your team treats as canonical
          components. Fragments Cloud flags bespoke render sites that should use
          these libraries instead.
        </p>

        <div className={s.panel}>
          {explicitSources.length > 0 && (
            <div className={settingsStyles.card}>
              {explicitSources.map((source, idx) => (
                <CanonicalSourceRow
                  key={sourceLabel(source)}
                  source={source}
                  onRemove={() => handleRemove(idx)}
                  disabled={submitting}
                />
              ))}
            </div>
          )}

          {showDerivedBadge && (
            <div className={settingsStyles.card}>
              <div className={s.row}>
                <SourceIdentity source={derived} />
                <span
                  className={s.derivedBadge}
                  title="Derived from your canonical design system binding. Edit to override."
                >
                  derived
                </span>
                <button
                  type="button"
                  className={s.overrideBtn}
                  onClick={handlePromoteDerived}
                  disabled={submitting}
                >
                  Edit to override
                </button>
              </div>
            </div>
          )}

          {explicitSources.length === 0 && !showDerivedBadge && (
            <div className={s.emptyState}>
              <p className={s.emptyTitle}>No canonical sources configured</p>
              <p className={s.emptyDesc}>
                Add one now so migration analysis has a source of truth when
                suggestions return.
              </p>
            </div>
          )}

          {adding ? (
            <AddCanonicalSourceForm
              onCancel={() => {
                setAdding(false);
                setMutationError(null);
              }}
              onSubmit={handleAdd}
              submitting={submitting}
            />
          ) : (
            <button
              type="button"
              className={s.addTrigger}
              onClick={() => setAdding(true)}
              disabled={submitting}
            >
              <Plus size={12} weight="bold" />
              Add source
            </button>
          )}

          {mutationError && (
            <p className={s.errorText} role="alert">
              {mutationError}
            </p>
          )}
        </div>
      </div>

      <div className={settingsStyles.section}>
        <p className={settingsStyles.sectionTitle}>PR-review advisor</p>
        <p className={settingsStyles.sectionDesc}>
          When opening or updating a pull request, post one inline review
          comment per cluster of raw HTML elements that have a canonical
          component in this project's map.
        </p>
        <div className={settingsStyles.card}>
          <div className={settingsStyles.cardRow}>
            <div className={settingsStyles.cardRowInfo}>
              <p className={settingsStyles.cardRowLabel}>
                Suggest canonical primitives on PRs
              </p>
              <p className={settingsStyles.cardRowDesc}>
                Auto- and overridden-band classifications only. Off disables the
                inline comments without affecting other governance.
              </p>
            </div>
            <button
              type="button"
              className={settingsStyles.toggle}
              role="switch"
              aria-checked={advisorEnabled ?? true}
              data-checked={(advisorEnabled ?? true) || undefined}
              onClick={handleAdvisorToggle}
              disabled={advisorSaving || advisorEnabled === undefined}
            >
              <span className={settingsStyles.toggleThumb} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
